// ============================================================
// Moteur PUR de la CLÔTURE DE L'ANNÉE DE CONGÉS — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-leave-closing.spec.ts.
// ============================================================
//
// Les congés payés ne se comptent pas par année civile. Ils s'acquièrent sur une PÉRIODE DE
// RÉFÉRENCE — du 1er juin au 31 mai à défaut d'accord (L3141-3), date réglable dans les règles de
// paie de l'établissement — et se prennent sur la suivante. À tout instant, deux années
// coexistent donc : celle qu'on acquiert (N) et celle qu'on solde (N-1).
//
// Ce module ne s'occupe QUE du passage de l'une à l'autre. Il répond à trois questions :
//
//   QUAND     la campagne de clôture s'ouvre-t-elle ? Un préavis avant la fin de la période
//             (30 jours par défaut : on en parle en mai pour une bascule au 1er juin), et elle ne
//             se referme JAMAIS d'elle-même — une clôture oubliée en juin doit encore se voir en
//             septembre, sinon elle ne se fait pas.
//
//   COMBIEN   que reste-t-il sur N-1 ? Report reçu de N-2, plus l'acquis de la période, moins ce
//             qui a été pris. Tout est RECALCULÉ depuis les absences : rien de cela n'est stocké.
//
//   ET APRÈS  ce solde est reporté, perdu, ou payé — et cela, c'est une DÉCISION. Elle s'écrit une
//             fois (hr_leave_period), elle se valide, et elle ne se recalcule plus. C'est la seule
//             valeur stockée du compteur de congés, et c'est précisément parce que ce n'en est
//             pas une : recalculer en 2030 le report de 2025 avec les règles de 2030 réécrirait
//             l'histoire d'un accord passé.
//
// LE REPORT N'EST PAS AUTOMATIQUE EN DROIT. Les congés non pris à la fin de la période sont en
// principe PERDUS (L3141-12 s.) — sauf report conventionnel, sauf impossibilité de les prendre du
// fait de l'employeur, sauf maladie (CJUE 2009, Cass. soc. 2024 : 15 mois de report). Le moteur ne
// trancher pas à la place du responsable : il PROPOSE (le réglage `carryover_default`), affiche ce
// qui se joue, et enregistre ce qui a été décidé — avec qui l'a décidé et quand.

import {
	HR_LEAVE_PERIOD_ACCEPTED, HR_LEAVE_PERIOD_CLOSED, HR_LEAVE_PERIOD_PROPOSED,
} from '@app/shared/constants';
import { HrLeavePeriod, hrCarriedDaysOf } from '@app/shared/_models/hr';
import { addDays } from './hr-schedule-engine';
import {
	DatRange, LeaveRules, TakenLeaveDay, acquiredLeaveDays, fractionationOf,
	leaveReferencePeriodOf, previousLeaveReferencePeriodOf,
} from './hr-leave-rights';

// ============================================================
// La fenêtre de campagne
// ============================================================

/// Où en est la clôture de la période N-1, à la date `dat` :
///   `upcoming`  la campagne n'est pas encore ouverte (on est loin de la fin de la période) ;
///   `open`      la fin approche ou vient de passer : c'est le moment d'arrêter les comptes ;
///   `late`      la période est finie depuis plus d'un mois et rien n'a été arrêté ;
///   `accepted`  le salarié a validé son solde, le responsable n'a pas encore clôturé ;
///   `closed`    l'arrêté est fait — l'année N reçoit son report.
export type LeaveClosingStatus = 'upcoming' | 'open' | 'late' | 'accepted' | 'closed';

export interface LeaveClosingWindow {
	period: DatRange;			// la période N-1, celle qu'on arrête
	next: DatRange;				// la période N, celle qui reçoit le report
	opens: string;				// jour d'ouverture de la campagne (fin de période − préavis)
	late_from: string;		// au-delà, la clôture est EN RETARD (un mois après la fin)
	open: boolean;				// la campagne est-elle ouverte à cette date ?
	late: boolean;
}

/// La période dont on s'occupe à la date `dat` — celle qui se termine, ou celle qui vient de se
/// terminer.
///
/// Le 2 mai 2026 (préavis de 30 jours), c'est déjà l'année 2025-2026 : elle s'achève dans un mois,
/// et c'est MAINTENANT qu'il faut dire au salarié « il vous reste 6 jours, posez-les avant le
/// 31 mai ou disons ce qu'on en fait ». Le 15 juin 2026, c'est encore elle — mais achevée, et le
/// solde n'est plus provisoire. Le 15 avril 2026, en revanche, l'année en cours a encore six
/// semaines devant elle : ce qui appelle une décision, c'est 2024-2025, restée ouverte.
export function leaveClosingPeriodOf(dat: string, rules: LeaveRules): DatRange {
	const current = leaveReferencePeriodOf(dat, rules);
	const opens = addDays(current.to, -rules.closing_notice_days);
	return dat >= opens ? current : previousLeaveReferencePeriodOf(dat, rules);
}

/// La campagne de clôture applicable à la date `dat`, sur la période que `leaveClosingPeriodOf`
/// désigne.
export function leaveClosingWindowOf(dat: string, rules: LeaveRules): LeaveClosingWindow {
	return leaveClosingWindowForPeriod(leaveClosingPeriodOf(dat, rules), dat, rules);
}

/// La même campagne, mais sur une période CHOISIE — l'écran permet de revenir sur une année
/// ancienne restée ouverte, et il faut alors son état à elle, pas celui de l'année courante.
export function leaveClosingWindowForPeriod(period: DatRange, dat: string, rules: LeaveRules): LeaveClosingWindow {
	// La campagne s'ouvre avant la FIN de la période, et ne se referme jamais : une clôture
	// oubliée en juin doit encore se voir en septembre, sinon elle ne se fait pas.
	const opens = addDays(period.to, -rules.closing_notice_days);
	const lateFrom = addDays(period.to, 31);
	return {
		period,
		next: leaveReferencePeriodOf(addDays(period.to, 1), rules),
		opens,
		late_from: lateFrom,
		open: dat >= opens,
		late: dat >= lateFrom,
	};
}

// ============================================================
// Le compte d'un salarié sur la période à clôturer
// ============================================================

export interface LeaveClosingRow {
	id_employee: number;
	period: DatRange;
	/// Report REÇU de la période N-2 (clôture arrêtée uniquement — une proposition ne reporte rien).
	carried_in: number;
	acquired_days: number;
	fractionation_days: number;
	taken_days: number;
	/// Ce qui reste à arbitrer : report entrant + acquis + fractionnement − pris.
	balance_days: number;
	/// La répartition PROPOSÉE au formulaire, d'après la règle applicable.
	proposed_carry: number;
	proposed_lost: number;
	/// La règle qui a produit cette proposition, et si elle vient de la FICHE du salarié plutôt
	/// que de l'établissement — le formulaire le dit, sinon une case cochée sur une fiche et un
	/// pré-remplissage inattendu n'ont aucun moyen de se rejoindre.
	policy: 'carry' | 'lost';
	policy_own: boolean;
	/// L'arrêté déjà enregistré pour cette période, s'il existe.
	closing: HrLeavePeriod | null;
	status: LeaveClosingStatus;
	/// Ce que la clôture transmet réellement à l'année N (0 tant qu'elle n'est pas arrêtée).
	carried_out: number;
	warnings: string[];
}

/// La règle de report PROPRE à un salarié (`hr_employee.data.cp.carryover`), ou null quand il suit
/// l'établissement — ce qui est le cas normal, et celui qu'il faut préserver : une valeur recopiée
/// sur chaque fiche y figerait la règle du jour, et changer la règle maison ne toucherait plus
/// personne (c'est exactement ce qui était arrivé au taux `data.cp.monthly`).
export function employeeCarryoverOf(data: any): 'carry' | 'lost' | null {
	const v = data?.cp?.carryover;
	return v === 'carry' || v === 'lost' ? v : null;
}

export interface LeaveClosingInput {
	id_employee: number;
	hire_date: string | null;
	/// Dernier jour de la relation de travail — null pour un salarié en poste. Borne l'acquisition
	/// (L3141-3) : un sorti le 30 novembre n'acquiert plus rien de décembre à mai.
	leave_date?: string | null;
	/// Règle de report propre au salarié ; absente, celle de l'établissement s'applique.
	/// Elle ne décide de rien : elle PRÉ-REMPLIT le formulaire d'arrêté (voir `proposed_carry`).
	carryover?: 'carry' | 'lost' | null;
	/// Jours de CP DÉCOMPTÉS du salarié (sortie de `hrCountedLeaveDays` sur les absences de type
	/// congé payé APPROUVÉES), avec la renonciation au fractionnement portée par la demande.
	taken: TakenLeaveDay[];
	/// Toutes les clôtures connues du salarié, tous exercices confondus.
	closings: HrLeavePeriod[];
	/// Mois d'arrêt maladie non professionnel sur la période (loi du 22 avril 2024) — 0 le plus
	/// souvent ; le compteur d'absences le fournira le jour où il saura les distinguer.
	sick_months?: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/// La clôture enregistrée pour une période donnée, ou null.
export function closingForPeriod(closings: HrLeavePeriod[], period: DatRange): HrLeavePeriod | null {
	return (closings ?? []).find(c => c.datstart === period.from) ?? null;
}

/// Le compte d'UN salarié sur la période à arrêter — tout recalculé, rien de stocké sauf le
/// report entrant, qui est lui-même un arrêté de compte antérieur.
export function buildLeaveClosingRow(
	input: LeaveClosingInput,
	window: LeaveClosingWindow,
	rules: LeaveRules,
): LeaveClosingRow {
	const period = window.period;
	const previous: DatRange = { from: addYearsDat(period.from, -1), to: addDays(period.from, -1) };

	const carriedIn = hrCarriedDaysOf(closingForPeriod(input.closings, previous));
	const inPeriod = (input.taken ?? []).filter(t => t.dat >= period.from && t.dat <= period.to);
	const acquisition = acquiredLeaveDays(period.from, period.to, input.hire_date, input.leave_date ?? null, rules, input.sick_months ?? 0);
	const fractionation = fractionationOf(inPeriod, rules);
	const taken = inPeriod.reduce((sum, t) => sum + t.days, 0);

	const fractionationDays = rules.fractionation_enabled ? fractionation.days : 0;
	const balance = round2(carriedIn + acquisition.acquired_days + fractionationDays - taken);

	const closing = closingForPeriod(input.closings, period);
	const warnings: string[] = [];

	// Un solde NÉGATIF veut dire qu'on a laissé poser plus que le droit : ce n'est pas une erreur
	// de calcul, c'est une avance sur l'année suivante, et elle doit se voir avant d'être oubliée.
	if (balance < 0) {
		warnings.push('Solde négatif : ' + daysLabel(-balance) + ' pris en avance sur l\'année suivante.');
	}
	if (!input.hire_date) {
		warnings.push('Date d\'embauche absente : l\'acquis est calculé sur la période entière.');
	}
	if (acquisition.capped) {
		warnings.push('Acquisition plafonnée à ' + rules.max_days + ' jours ' + rules.acquisition_unit + '.');
	}
	if (carriedIn > 0) {
		warnings.push(daysLabel(carriedIn) + ' reportés de l\'année précédente sont compris dans ce solde.');
	}

	// La PROPOSITION du formulaire. Un solde négatif ne se reporte pas : il n'y a rien à répartir,
	// la dette se règle sur le compteur de l'année suivante, pas dans cet arrêté.
	//
	// La règle PROPRE au salarié l'emporte sur celle de l'établissement — mais elle ne fait que
	// pré-remplir : ce qui décide reste l'arrêté, année par année. La raison d'un report
	// (maladie, congés refusés par l'employeur) appartient à l'ANNÉE, pas à la personne.
	const toShare = Math.max(0, balance);
	const policy = input.carryover ?? rules.carryover_default;
	const proposedCarry = policy === 'lost' ? 0 : toShare;

	return {
		id_employee: input.id_employee,
		period,
		carried_in: carriedIn,
		acquired_days: acquisition.acquired_days,
		fractionation_days: fractionationDays,
		taken_days: round2(taken),
		balance_days: balance,
		proposed_carry: round2(proposedCarry),
		proposed_lost: round2(toShare - proposedCarry),
		policy,
		policy_own: !!input.carryover,
		closing,
		status: closingStatusOf(closing, window),
		carried_out: hrCarriedDaysOf(closing),
		warnings,
	};
}

/// L'état d'une clôture : ce qui est enregistré l'emporte sur le calendrier — une période arrêtée
/// en avril n'est pas « à venir » parce qu'on est en mars.
export function closingStatusOf(closing: HrLeavePeriod | null, window: LeaveClosingWindow): LeaveClosingStatus {
	if (closing?.state === HR_LEAVE_PERIOD_CLOSED) return 'closed';
	if (closing?.state === HR_LEAVE_PERIOD_ACCEPTED) return 'accepted';
	if (window.late) return 'late';
	return window.open ? 'open' : 'upcoming';
}

// ============================================================
// Le formulaire — ce qu'on décide du solde
// ============================================================

export interface LeaveClosingForm {
	carried_days: number;
	lost_days: number;
	paid_days: number;
	comment: string | null;
}

/// La répartition tient-elle ? Rend le motif de refus, ou null.
///
/// La règle est arithmétique : ce qui reste est reporté, perdu ou payé, et rien ne se perd en
/// route. On tolère un centième d'écart — les demi-journées et les 2,08 j/mois produisent des
/// arrondis, et refuser un arrêté pour 0,004 jour serait absurde.
export function leaveClosingIssue(form: LeaveClosingForm, row: LeaveClosingRow): string | null {
	const carry = +form.carried_days || 0;
	const lost = +form.lost_days || 0;
	const paid = +form.paid_days || 0;

	if (carry < 0 || lost < 0 || paid < 0) {
		return 'Un arrêté de compte ne porte pas de jours négatifs.';
	}
	const target = Math.max(0, row.balance_days);
	const total = round2(carry + lost + paid);
	if (Math.abs(total - target) > 0.01) {
		return 'La répartition doit faire ' + daysLabel(target) + ' — ' + daysLabel(total)
			+ ' répartis (report ' + fmt(carry) + ' + perdus ' + fmt(lost) + ' + payés ' + fmt(paid) + ').';
	}
	return null;
}

/// Le payload de l'event 41 — les mémos `acquired_days` / `taken_days` figent l'arrêté tel qu'il a
/// été signé, pour qu'on puisse le relire dans dix ans sans dépendre d'un recalcul.
export function leaveClosingPayload(
	row: LeaveClosingRow,
	form: LeaveClosingForm,
	state: number = HR_LEAVE_PERIOD_CLOSED,
): any {
	return {
		id: row.closing?.id ?? 0,
		id_employee: row.id_employee,
		datstart: row.period.from,
		datend: row.period.to,
		acquired_days: round2(row.carried_in + row.acquired_days + row.fractionation_days),
		taken_days: row.taken_days,
		carried_days: round2(+form.carried_days || 0),
		lost_days: round2(+form.lost_days || 0),
		paid_days: round2(+form.paid_days || 0),
		state,
		comment: (form.comment ?? '').toString().trim() || null,
		data: null,
	};
}

// ============================================================
// Vue d'ensemble — ce que la campagne affiche en tête d'écran
// ============================================================

export interface LeaveClosingSummary {
	total: number;
	closed: number;
	accepted: number;
	pending: number;			// ni acceptée ni clôturée : c'est le chiffre qui appelle une action
	carried_days: number;	// jours effectivement reportés sur l'année N
	lost_days: number;
	balance_days: number;	// ce qui reste à arbitrer, tous salariés confondus
}

export function leaveClosingSummary(rows: LeaveClosingRow[]): LeaveClosingSummary {
	const s: LeaveClosingSummary = {
		total: rows.length, closed: 0, accepted: 0, pending: 0,
		carried_days: 0, lost_days: 0, balance_days: 0,
	};
	for (const r of rows) {
		if (r.status === 'closed') {
			s.closed++;
			s.carried_days += r.carried_out;
			s.lost_days += (+r.closing?.lost_days! || 0);
		} else {
			if (r.status === 'accepted') s.accepted++;
			s.pending++;
			s.balance_days += Math.max(0, r.balance_days);
		}
	}
	s.carried_days = round2(s.carried_days);
	s.lost_days = round2(s.lost_days);
	s.balance_days = round2(s.balance_days);
	return s;
}

/// « du 01/06/2025 au 31/05/2026 » — la période telle qu'elle se lit sur un courrier.
export function leavePeriodLabel(p: DatRange): string {
	return 'du ' + frDat(p.from) + ' au ' + frDat(p.to);
}

/// « 2025-2026 » — le nom court de l'année de congés, celui dont on parle à l'oral.
export function leavePeriodShortLabel(p: DatRange): string {
	const y1 = p.from.slice(0, 4);
	const y2 = p.to.slice(0, 4);
	return y1 === y2 ? y1 : (y1 + '-' + y2);
}

// ============================================================
// Petits formatages partagés
// ============================================================

export function daysLabel(days: number): string {
	const n = round2(days);
	if (n === 0.5) return '½ jour';
	return fmt(n) + ' jour' + (n > 1 ? 's' : '');
}

function fmt(n: number): string {
	return (Math.round(n * 100) / 100).toString().replace('.', ',');
}

function frDat(dat: string): string {
	return dat ? (dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4)) : '';
}

function addYearsDat(dat: string, n: number): string {
	return (+dat.slice(0, 4) + n) + dat.slice(4);
}

export { HR_LEAVE_PERIOD_ACCEPTED, HR_LEAVE_PERIOD_CLOSED, HR_LEAVE_PERIOD_PROPOSED };
